import * as _ from 'lodash';

import {
  BucketedRunsDeltaQueryQuery,
  BucketedRunsDeltaQueryQueryResult,
} from '../../../generated/graphql';
import {getLastUpdatedWithWindow} from '../../../util/runDeltas';
import * as Run from '../../../util/runs';
import {BucketedLine} from '../types';
import {startPerfTimer} from './../../../util/profiler';

/**
 * The idea here is to manage the state outside of the component that does the rendering and outside of any complications with the React lifecycle.
 *
 * Design principles:
 * 1. The state is managed via a reducer for easy profiling, debugging, and testing
 * 2. We should seek to process data as minimally as possible to speed up JS execution
 * 3. There is as minimal a dependency on Apollo and its hooks as possible
 * 4. The state exported from the engine should be designed to minimize the need for expensive processing
 */

type BucketDelta = NonNullable<
  NonNullable<BucketedRunsDeltaQueryQuery['project']>['runs']
>['deltas'];

type State = {
  context: Record<string, any>;
  error: any;
  lastUpdated: string;
  loading: boolean;
  orderedRunIds: string[];
  runsById: Record<string, BucketedLine>;
  totalRuns: number;
  xAxis: string;
};

export const initialState: State = {
  context: {},
  error: {},
  lastUpdated: new Date(0).toISOString(),
  loading: false,
  orderedRunIds: [],
  runsById: {},
  totalRuns: 0,
  xAxis: '',
};

type Actions =
  /**
   * Set additional context helpful for debugging
   */
  | {type: 'context'; payload: Record<string, any>}
  /**
   * Fetching occurs when the query is in flight
   */
  | {type: 'fetch'}
  /**
   *
   */
  | {type: 'error'; error: BucketedRunsDeltaQueryQueryResult['error']}
  /**
   * Reload is used to reset the state of the data. This is needed when we want to clear out the store for things like:
   * - changing the x-axis
   * - changing the filters
   * - zooming
   * - etc
   */
  | {
      type: 'reload';
      payload: {
        xAxis: string; // the x-axis value is needed to build the state off the returned data
      };
    }
  /**
   * Update is when new data is written to the reducer. Each fetch returns a list of delta ops to apply
   */
  | {
      type: 'update';
      payload: {
        delta?: NonNullable<BucketDelta>['delta'];
        order?: NonNullable<BucketDelta>['order'];
        totalRuns: number;
      };
    };

export const outliersReducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case 'context': {
      return {
        ...state,
        context: action.payload,
      };
    }
    case 'error': {
      return {
        ...state,
        error: action.error,
        loading: false,
      };
    }
    case 'fetch': {
      return {
        ...state,
        loading: true,
      };
    }

    case 'reload': {
      return {
        ...state,
        error: {},
        lastUpdated: new Date(0).toISOString(),
        loading: true,
        orderedRunIds: [],
        runsById: {},
        xAxis: action.payload.xAxis,
      };
    }

    /**
     * Update is the most complicated path. The API returns two kinds of diff
     * ops: Insert and Replace - we can treat them as the same because either
     * way we need to update the associated record for that ID
     *
     * Caveats:
     * 1. Because the API returns the proper order as a list of IDs, we need to
     * do a deep comparison so that we don't thrash this and cause rerendering
     * even when the lists are equal
     *
     * 2. Any item that's not part of the new ordered ID list is functionally a
     * delete OP, so we'll need to refresh the reference for `runsById`
     *
     * 3. Update the record for each associated run that has a diffOp. We might
     * want to do this more surgically in the future but for now we want to make
     * sure we don't hang on to any data that's been changed as part of the diffOp
     *
     * 4. Calculate the `lastUpdated` time - this is what allows us to query for
     * just the diffs in the backend
     */
    case 'update': {
      const {endPerfTimer} = startPerfTimer('outliersReducer:update');

      const isOrderUnchanged = _.isEqual(
        state.orderedRunIds,
        action.payload.order
      );
      // preserve the reference if there are no changes to orders
      const stableOrder =
        (isOrderUnchanged ? state.orderedRunIds : action.payload.order) ?? [];

      const {delta = [], order = [], totalRuns} = action.payload;

      const deleteOps = state.orderedRunIds.filter(rId => !order.includes(rId));

      let updatedRunsById = state.runsById;
      // if there are no new ops and no deletes, return the state as is
      if (delta.length === 0 && deleteOps.length === 0) {
        return {
          ...state,
          loading: false,
          orderedRunIds: stableOrder,
          totalRuns,
        };
      } else {
        // new diffOps or deleteOps means we need to refresh the reference for `runsById`
        updatedRunsById = Object.assign({}, state.runsById);
        deleteOps.forEach(rId => {
          delete updatedRunsById[rId];
        });
      }

      delta.forEach(runDelta => {
        if (!runDelta.run) {
          console.error('Bucketed Delta Op returned without valid run');
          return;
        }

        try {
          const parsedRun = Run.fromJson(runDelta.run);
          if (parsedRun == null) {
            console.error(
              'Unable to parse run from server in outliers reducer'
            );
            return;
          }

          // Update the entire record for any diffOp
          updatedRunsById[runDelta.run.id] = {
            ...(parsedRun as unknown as BucketedLine),
            history: runDelta.run.bucketedHistory.map(h => {
              h.forEach((k: Record<string, unknown>) => {
                // system metrics charts don't return in bucketing mode, so those values won't be keyed
                // with the `-Avg` suffix. We still want to handle them in outliers
                if (k[`${state.xAxis}Avg`]) {
                  k[state.xAxis] = k[`${state.xAxis}Avg`];
                }
              });
              return h;
            }),
            keyInfo: {
              keys: {},
              sets: [],
            },
          };
        } catch (e) {
          console.error('Error parsing run data in outliers reducer', e);
        }
      });

      const newState = {
        ...state,
        lastUpdated: getLastUpdateTime(updatedRunsById),
        loading: false,
        runsById: updatedRunsById,
        orderedRunIds: stableOrder,
        totalRuns,
      };
      endPerfTimer();
      return newState;
    }
    default: {
      return state;
    }
  }
};

export function getLastUpdateTime(runs: Record<string, BucketedLine>) {
  // getting invalid time value, suspect this is because of 0 length updatedRunsById objects
  // https://weights-biases.sentry.io/issues/5354440693/?project=1201719&query=is%3Aunresolved+issue.priority%3A%5Bhigh%2C+medium%5D&referrer=issue-stream&statsPeriod=4d&stream_index=4&utc=true
  const runArray = Object.values(runs);
  let lastUpdated: string;
  try {
    const mostRecentUpdate = Math.max(
      ...runArray
        .filter(r => {
          if (isNaN(Date.parse(r.updatedAt))) {
            console.error('Non-date date encountered in run', {
              name: r.name,
              id: r.id,
              updatedAt: r.updatedAt,
            });
            return false;
          }
          return true;
        })
        .map(r => new Date(r.updatedAt).getTime())
    );
    const mostRecentAsDate = new Date(mostRecentUpdate).toISOString();
    lastUpdated = getLastUpdatedWithWindow(mostRecentAsDate);
  } catch (e) {
    // 0 length run arrays will error, but they're expected
    // only log errors if this isn't the case
    if (runArray.length > 0) {
      console.error('Error parsing lastUpdated date in outliers reducer', {
        // @ts-expect-error
        message: e.message ? e.message : JSON.stringify(e),
        numRuns: Object.values(runs).length,
        dates: JSON.stringify(runArray.map(o => o.updatedAt).join(', ')),
      });
    }

    lastUpdated = new Date(0).toISOString();
  }

  return lastUpdated;
}
